import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import type { PortalProfile } from '@/portal/api/portalApi';
import { PORTAL_SESSION_MAX_AGE_MS } from '@/portal/config';

export interface PortalSessionState {
  token: string | null;
  profile: PortalProfile | null;
  issuedAt: number | null;
  startSession: (token: string, profile: PortalProfile) => void;
  updateProfile: (profile: PortalProfile) => void;
  endSession: () => void;
}

export const isSessionActive = (
  session: Pick<PortalSessionState, 'token' | 'issuedAt'>,
  now: number = Date.now(),
): boolean =>
  Boolean(session.token) &&
  session.issuedAt !== null &&
  now - session.issuedAt < PORTAL_SESSION_MAX_AGE_MS;

// Private browsing and blocked storage can throw; the portal then simply has no saved session.
const safeSessionStorage: StateStorage = {
  getItem: (name) => {
    try {
      return sessionStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      sessionStorage.setItem(name, value);
    } catch {
      // Ignored: the session just won't survive a reload.
    }
  },
  removeItem: (name) => {
    try {
      sessionStorage.removeItem(name);
    } catch {
      // Ignored.
    }
  },
};

export const usePortalSession = create<PortalSessionState>()(
  persist(
    (set) => ({
      token: null,
      profile: null,
      issuedAt: null,
      startSession: (token, profile) => set({ token, profile, issuedAt: Date.now() }),
      updateProfile: (profile) => set({ profile }),
      endSession: () => set({ token: null, profile: null, issuedAt: null }),
    }),
    {
      name: 'elhamy-portal-session',
      storage: createJSONStorage(() => safeSessionStorage),
    },
  ),
);
